import { useEffect, useRef, useState } from 'react';

interface LazyVideoProps {
  src: string;
  /** Optional WebM sibling source — preferred by Chrome/Firefox/Edge when present. */
  srcWebm?: string;
  poster?: string;
  className?: string;
  /** CSS rootMargin for the intersection observer — start loading this much before entering viewport */
  rootMargin?: string;
  /** If true, keep the video playing even when out of view (wastes bandwidth/CPU). Default: false */
  keepPlayingOutOfView?: boolean;
  /**
   * `<video preload>` attribute. Default `metadata` — pulls only ~100 KB
   * of headers + the first frame so the poster swap is invisible, but
   * doesn't stream the full mp4 until playback starts. Bump to `auto`
   * for the page's hero video where you want it ready instantly.
   */
  preload?: 'none' | 'metadata' | 'auto';
}

/**
 * Drop-in replacement for autoplaying <video> that does NOT download the video
 * until the element scrolls near the viewport. Mounts the real <video> element
 * lazily via IntersectionObserver. Pauses when scrolled away.
 */
export default function LazyVideo({
  src,
  srcWebm,
  poster,
  className = '',
  rootMargin = '400px',
  keepPlayingOutOfView = false,
  preload = 'metadata',
}: LazyVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldMount, setShouldMount] = useState(false);

  // Mount the video element when the container scrolls near viewport
  useEffect(() => {
    if (shouldMount) return;
    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldMount(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, shouldMount]);

  // Once mounted, pause when scrolled away to save CPU/battery
  useEffect(() => {
    if (!shouldMount || keepPlayingOutOfView) return;
    const videoEl = videoRef.current;
    if (!videoEl || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            videoEl.play().catch(() => {});
          } else {
            videoEl.pause();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(videoEl);
    return () => observer.disconnect();
  }, [shouldMount, keepPlayingOutOfView]);

  return (
    <div ref={containerRef} className={className}>
      {shouldMount ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload={preload}
          poster={poster}
          className="h-full w-full object-cover"
        >
          {/* WebM first so supporting browsers pick the smaller file. */}
          {srcWebm && <source src={srcWebm} type="video/webm" />}
          <source src={src} type="video/mp4" />
        </video>
      ) : poster ? (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="h-full w-full bg-foreground-deep" />
      )}
    </div>
  );
}
